import React, { FunctionComponent, useState } from 'react';

import _ from 'lodash';
import { DateTime } from 'luxon';
import { Button, Card, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AutoSizer, Grid } from 'react-virtualized';

import { ModuleId } from 'admin/modules/Modules';
import { hasModule } from 'admin/tenants/Tenants';
import { useProfile } from 'auth/Auth';
import { ButtonVariant, Size } from 'core/util/Bootstrap';
import {
    LocationUid,
    LocationResponse,
    ScheduleDateObject,
    SchedulePostResponse,
    SchedulePostsResponse,
    SocialPostState,
    SourceName,
    SourceResult,
    SelectedSource,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { getScheduleDateTimeDisplayObject } from 'social/publisher/util/Util';

import 'social/publisher/post-summary/PostSummary.scss';

interface Props {
    isShowPostSummary: boolean;
    schedulePostResponse: SchedulePostResponse | SchedulePostsResponse | undefined;
    onDone?: any;
    isDuplicatePostEnabled: boolean;
    selectedSources: SelectedSource;
}
type PostStatus = 'SCHEDULED' | 'ERROR' | 'PENDING';
type PostStatusDisplay = {
    status: PostStatus;
    message: string;
};
type PostStatusBySource = Record<SourceName, PostStatusDisplay | undefined>;
type LocationRow = {
    locationName: string;
    slpStatusBySource: PostStatusBySource;
    scheduleDateTimeDisplay: string;
    spCalendarLink: string;
    spDeepLink: string;
};
type ScheduledSlpsBySource = Record<SourceName, number>;
type ResponseSummary = {
    locations: LocationUid[];
    sources: SourceName[];
    scheduledSlpsBySource: ScheduledSlpsBySource;
    pendingSlps: 0;
    failedSlps: 0;
};
const postStatusIconMap: Record<PostStatus, string> = {
    SCHEDULED: 'icon-clock',
    ERROR: 'icon-exclamation-circle',
    PENDING: 'icon-question-circle',
};
const getSpDeepLink = (postId: string): string => {
    return `${process.env.REACT_APP_OLD_STACK_HOST}/social2/calendar2/post/${postId}`;
};
const getSpCalendarLink = (postDate: ScheduleDateObject): string => {
    const {
        postDate: { year, month, dayOfMonth, hour, minute, second, timezone },
    } = postDate;
    // SPs in calendar day view (SP) are grouped by day based on the current user's browser timezone (if you are in CT, May 1st bucket will contain posts that are scheduled between May 1st, 00:00 to 24:00 CT)
    // local timezone: at SLP level, each location will uses its own timezone for publishing. At SP level, PST (GMT-8) is used for converting to browser timezone when group by day is done in the back end. global timezone: user selected timezone is used when group by day is done. Either way, we need to convert the scheduled date time to browser time.
    const spCalendarDate = DateTime.fromObject({
        year,
        month,
        day: dayOfMonth,
        hour,
        minute,
        second,
        zone: `UTC${timezone}`,
    }).setZone(DateTime.local().zoneName);

    return `${process.env.REACT_APP_OLD_STACK_HOST}/social2/calendar2/day/${spCalendarDate.year}/${spCalendarDate.month}/${spCalendarDate.day}`;
};
const getScheduledSlps = (scheduledSlpsBySource: ScheduledSlpsBySource): number => {
    let sum = 0;

    _.forEach(scheduledSlpsBySource, (v, k) => {
        sum += v;
    });

    return sum;
};

// type guard
const isMultiPublishResponse = (
    response: SchedulePostResponse | SchedulePostsResponse,
): response is SchedulePostsResponse => {
    return (response as SchedulePostsResponse).schedulePostV2ResponseList !== undefined;
};

export const PostSummary: FunctionComponent<Props> = ({
    isShowPostSummary,
    schedulePostResponse,
    onDone,
    isDuplicatePostEnabled,
    selectedSources,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const [isDetailsShowing, setIsDetailsShowing] = useState(true);
    const [{ profile }] = useProfile();

    const getScheduleDateTimeDisplay = (scheduleDate: ScheduleDateObject): string => {
        const scheduleDateTimeDisplayObject = getScheduleDateTimeDisplayObject(scheduleDate);

        return `${scheduleDateTimeDisplayObject.dateTimeDisplay} ${t(scheduleDateTimeDisplayObject.timezoneDisplay)}`;
    };
    const handleToggleDetails = (): void => {
        const isDetailsShowingNew = !isDetailsShowing;

        setIsDetailsShowing(isDetailsShowingNew);
    };
    const handleCloseModal = (): void => {
        dispatch({ type: 'set-is-show-post-summary', payload: { value: false } });
        if (onDone && typeof onDone === 'function') {
            onDone();
        }
    };
    const handleDuplicatePost = (): void => {
        dispatch({ type: 'set-duplicate-post-publish-mode', payload: null });
        dispatch({ type: 'set-is-show-post-summary', payload: { value: false } });
    };
    const getSlpStatus = (sourceResults: SourceResult[]): PostStatusBySource => {
        const slpStatusBySource: PostStatusBySource = {
            FACEBOOK: undefined,
            INSTAGRAM: undefined,
            TWITTER: undefined,
            GOOGLE_PLACES: undefined,
            LINKEDIN: undefined,
            YOUTUBE: undefined,
            IG_STORIES: undefined,
            IG_REELS: undefined,
            TIKTOK: undefined,
        };

        sourceResults.forEach((sourceResult: SourceResult): void => {
            let { source } = sourceResult;

            if (sourceResult.source === 'INSTAGRAM') {
                if (selectedSources.IG_STORIES) {
                    source = 'IG_STORIES';
                } else if (selectedSources.IG_REELS) {
                    source = 'IG_REELS';
                } else {
                    source = 'INSTAGRAM';
                }
            }

            // PostSucceeded = published, Approved = scheduled. Currently BE sends PostSucceeded in schedulePostResponse in both cases. Separate these in the future if this changes
            if (
                sourceResult.slpState === SocialPostState.Approved ||
                sourceResult.slpState === SocialPostState.PostSucceeded
            ) {
                slpStatusBySource[source] = {
                    status: 'SCHEDULED',
                    message: 'SCHEDULE_POST_SUCCESS',
                };
            } else if (sourceResult.slpState === SocialPostState.PendingApproval) {
                slpStatusBySource[source] = {
                    status: 'PENDING',
                    message: 'PENDING_APPROVAL',
                };
            } else if (sourceResult.slpState === SocialPostState.PostFailed) {
                slpStatusBySource[source] = {
                    status: 'ERROR',
                    message: _.get(sourceResult, 'response.errors[0].message') || 'UNKNOWN_ERROR',
                };
            }
        });

        return slpStatusBySource;
    };

    const getSummary = (locationSaves: LocationResponse[]): ResponseSummary => {
        const summary: ResponseSummary = {
            locations: [],
            sources: [],
            pendingSlps: 0,
            scheduledSlpsBySource: {
                FACEBOOK: 0,
                INSTAGRAM: 0,
                TWITTER: 0,
                GOOGLE_PLACES: 0,
                LINKEDIN: 0,
                YOUTUBE: 0,
                IG_STORIES: 0,
                IG_REELS: 0,
                TIKTOK: 0,
            },
            failedSlps: 0,
        };

        locationSaves.forEach((result): void => {
            const { locationUid, sourceResults } = result;

            if (!_.includes(summary.locations, locationUid)) {
                summary.locations.push(locationUid);
            }

            sourceResults.forEach((sourceResult: SourceResult): void => {
                const { source } = sourceResult;

                if (!_.includes(summary.sources, source)) {
                    if (source === 'INSTAGRAM') {
                        if (selectedSources.IG_STORIES && !_.includes(summary.sources, 'IG_STORIES')) {
                            summary.sources.push('IG_STORIES');
                        } else if (selectedSources.IG_REELS && !_.includes(summary.sources, 'IG_REELS')) {
                            summary.sources.push('IG_REELS');
                        } else if (selectedSources.INSTAGRAM && !_.includes(summary.sources, 'INSTAGRAM')) {
                            summary.sources.push('INSTAGRAM');
                        }
                    } else {
                        summary.sources.push(source);
                    }
                }

                if (
                    sourceResult.slpState === SocialPostState.Approved ||
                    sourceResult.slpState === SocialPostState.PostSucceeded
                ) {
                    if (source === 'INSTAGRAM') {
                        if (selectedSources.IG_STORIES) {
                            summary.scheduledSlpsBySource.IG_STORIES += 1;
                        } else if (selectedSources.IG_REELS) {
                            summary.scheduledSlpsBySource.IG_REELS += 1;
                        } else {
                            summary.scheduledSlpsBySource.INSTAGRAM += 1;
                        }
                    } else {
                        summary.scheduledSlpsBySource[source] += 1;
                    }
                } else if (sourceResult.slpState === SocialPostState.PendingApproval) {
                    summary.pendingSlps += 1;
                } else if (sourceResult.slpState === SocialPostState.PostFailed) {
                    summary.failedSlps += 1;
                }
            });
        });

        return summary;
    };

    const getLocationRows = (response: SchedulePostResponse): LocationRow[] => {
        const locationRows: LocationRow[] = [];
        const { postDate, id, locationSaves } = response;
        const scheduleDateTimeDisplay = getScheduleDateTimeDisplay(postDate);
        const spCalendarLink = getSpCalendarLink(postDate);
        const spDeepLink = getSpDeepLink(id);

        if (locationSaves) {
            locationSaves.forEach((result): void => {
                const { locationName, sourceResults } = result;
                const slpStatusBySource = getSlpStatus(sourceResults);
                const locationRow: LocationRow = {
                    locationName,
                    slpStatusBySource,
                    scheduleDateTimeDisplay,
                    spCalendarLink,
                    spDeepLink,
                };

                locationRows.push(locationRow);
            });
        }

        return locationRows;
    };

    let locationSaves: LocationResponse[] = [];
    let locationRows: LocationRow[] = [];

    if (schedulePostResponse) {
        if (isMultiPublishResponse(schedulePostResponse)) {
            schedulePostResponse.schedulePostV2ResponseList.forEach((el) => {
                if (el.locationSaves) {
                    locationSaves = [...locationSaves, ...el.locationSaves];
                    locationRows = [...locationRows, ...getLocationRows(el)];
                }
            });
        } else if (schedulePostResponse.locationSaves) {
            locationSaves = schedulePostResponse.locationSaves;
            locationRows = getLocationRows(schedulePostResponse);
        }
    }

    const summary = getSummary(locationSaves);

    const metricsDisplay = [
        {
            label: 'PENDING',
            value: summary.pendingSlps,
            icon: 'icon-question-circle',
        },
        {
            label: 'SCHEDULED',
            value: getScheduledSlps(summary.scheduledSlpsBySource),
            icon: 'icon-clock',
        },
        {
            label: 'ERRORS',
            value: summary.failedSlps,
            icon: 'icon-exclamation-circle',
        },
        {
            label: 'LOCATIONS',
            value: summary.locations.length,
            icon: 'icon-map-marker-plus',
        },
        {
            label: 'SOURCES',
            value: summary.sources.length,
            icon: 'icon-share-alt',
        },
    ];
    const displayHeader = [
        <div className="location-grid-header-name" key="name">
            {t('LOCATIONS_COUNT', { count: summary.locations.length })}
        </div>,
        <div className="location-grid-header-date" key="date">
            {t('DATE')}
        </div>,
        <div className="location-grid-header-sources" key="sources">
            {summary.sources.map(
                (sourceName: SourceName): JSX.Element => (
                    <div className="location-grid-header-source" key={sourceName}>
                        <div className="location-grid-header-source-spacer">{}</div>
                        <div className={`location-grid-header-source-icon social-icon ${sourceName.toLowerCase()}`} />
                        <div className="location-grid-header-source-count">
                            <span>{summary.scheduledSlpsBySource[sourceName]}</span>
                        </div>
                    </div>
                ),
            )}
        </div>,
        <div className="location-grid-header-actions" key="actions">
            {t('ACTIONS')}
        </div>,
    ];
    const locationRowsDisplay = locationRows.map((el) => {
        const { locationName, scheduleDateTimeDisplay, spCalendarLink, spDeepLink, slpStatusBySource } = el;
        const locationNameCol = <div className="location-grid-row-location">{locationName}</div>;
        const dateCol = (
            <div className="location-grid-row-date">
                <i className="icon-calendar" />
                <a href={spCalendarLink}>{scheduleDateTimeDisplay}</a>
            </div>
        );
        const sourcesCol = (
            <div className="location-grid-row-sources">
                {summary.sources.map((source: SourceName): JSX.Element => {
                    if (!slpStatusBySource[source]) {
                        return <div className="location-grid-row-source-spacer">{}</div>;
                    }
                    const { status, message } = slpStatusBySource[source] as PostStatusDisplay;
                    const statusIcon = (
                        <OverlayTrigger
                            key={`${source}-${status}`}
                            placement="top"
                            overlay={<Tooltip id={`tooltip-${source}-${status}`}>{t(message)}</Tooltip>}
                        >
                            <i className={postStatusIconMap[status]} />
                        </OverlayTrigger>
                    );

                    return statusIcon;
                })}
            </div>
        );
        const actionsCol = (
            <div className="location-grid-row-actions">
                {hasModule(profile?.tenant, ModuleId.Social2) && (
                    <a href={spDeepLink}>
                        <i className="icon-calendar" />
                    </a>
                )}
            </div>
        );

        return [locationNameCol, dateCol, sourcesCol, actionsCol];
    });
    // Virtualized Grid display values, in pixels
    const GRID_WIDTH = 1108;
    const GRID_HEIGHT = 240;
    const ROW_HEIGHT = 55;
    const COLUMN_COUNT = 4;
    const getGridColumnWidth = ({ index }: { index: number }): number => {
        switch (index) {
            // Location Name
            case 0:
                return 300;
            // Date
            case 1:
                return 300;
            // Sources
            case 2:
                return 300;
            // Actions
            case 3:
                return 208;
            default:
                return 100;
        }
    };
    const gridCellRenderer = ({
        columnIndex,
        key,
        rowIndex,
        style,
    }: {
        columnIndex: number;
        key: string;
        rowIndex: number;
        style: any;
    }): JSX.Element => {
        return (
            <div key={key} style={style}>
                {locationRowsDisplay[rowIndex][columnIndex]}
            </div>
        );
    };
    let displayGrid;

    if (locationRowsDisplay.length === 0) {
        displayGrid = <div className="location-grid-content-empty">{t('NO_LOCATIONS_TO_DISPLAY')}</div>;
    } else {
        displayGrid = (
            <AutoSizer>
                {({ height, width }): JSX.Element => (
                    <Grid
                        cellRenderer={gridCellRenderer}
                        columnCount={COLUMN_COUNT}
                        columnWidth={getGridColumnWidth}
                        height={GRID_HEIGHT}
                        rowCount={locationRowsDisplay.length}
                        rowHeight={ROW_HEIGHT}
                        width={GRID_WIDTH}
                    />
                )}
            </AutoSizer>
        );
    }

    const isPartiallyScheduled = (schedulePostResponse: SchedulePostResponse): boolean => {
        if (!schedulePostResponse) {
            return false;
        }

        const { response } = schedulePostResponse;

        if (response.message) {
            return response.message.includes('partially'); // todo: backend to provide a more concrete way to identify this scenario.
        }

        return false;
    };

    return (
        <div className="r4e-social-post-summary">
            <Modal
                show={isShowPostSummary}
                onHide={handleCloseModal}
                className="r4e-social-post-summary-modal"
                backdrop="static"
                size={Size.XLarge}
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('POST_SUMMARY_TITLE')}
                        {isPartiallyScheduled(schedulePostResponse as SchedulePostResponse) && (
                            <div className="normal regular">{t('POST_SUMMARY_SUBTITLE_PARTIALLY_SCHEDULED')}</div>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="post-summary-metrics">
                        {metricsDisplay.map(
                            (metric): JSX.Element => (
                                <Card className="post-summary-metric" key={metric.label}>
                                    <Card.Body>
                                        <div className="metric">
                                            <div className="metric-label">{t(metric.label)}</div>
                                            <div className="metric-value">{metric.value}</div>
                                        </div>
                                        <i className={`icon ${metric.icon}`} />
                                    </Card.Body>
                                </Card>
                            ),
                        )}
                    </div>
                    <Button variant="link" onClick={handleToggleDetails}>
                        {isDetailsShowing ? t('HIDE_DETAILS') : t('SHOW_DETAILS')}
                    </Button>
                    {isDetailsShowing && (
                        <div className="post-summary-location-details">
                            <div className="location-grid-header">{displayHeader}</div>
                            <div className="location-grid-content">{displayGrid}</div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {isDuplicatePostEnabled && (
                        <Button variant={ButtonVariant.Secondary} onClick={handleDuplicatePost}>
                            {t('DUPLICATE_POST')}
                        </Button>
                    )}
                    <Button variant={ButtonVariant.Primary} onClick={handleCloseModal} className="done-btn">
                        {t('DONE')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
